import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { placeAgainstAnchor } from "../shared/anchoredPlacement";

/**
 * BdrMenu — a menu of ACTIONS hanging off a trigger.
 *
 * Deliberately not the same surface as BdrSelectPopup, because they answer
 * different questions:
 *
 *   BdrSelectPopup  "which value goes in this field?"   — --bdr-surface-solid
 *   BdrMenu         "where do I go / what do I do?"     — --bdr-grad-dark-card
 *
 * The menu's token is layered over `--bdr-surface-backing` in CSS, and that is
 * load-bearing: both of its stops carry alpha, so used bare on a floating
 * panel the page would read through its own items. A menu floats over
 * arbitrary content, so it has to OCCLUDE — the same rule recorded app-wide
 * as "a modal panel must be opaque".
 *
 * Tried the flat `--bdr-surface-solid` look here on 2026-10-01 and reverted it
 * the same day: the owner preferred the gradient card. Left recorded so it is
 * not proposed a third time.
 *
 * It replaces MUI's `Menu`, which could not be styled to match anything here
 * and shipped a white paper with black text on a blue-purple app.
 *
 * Load-bearing, same as the select popup:
 *
 *  1. PORTALLED to <body>, positioned `fixed` from the trigger's rect. Inline,
 *     it would be clipped by the first ancestor with `overflow` — a modal body
 *     or a navbar.
 *  2. mousedown does NOT propagate. A React portal bubbles through the REACT
 *     tree, so without this a click on the menu reaches a host modal's
 *     backdrop handler, which sees a target outside its panel and closes it.
 *  3. Focus moves INTO the menu, unlike the select popup. A select keeps focus
 *     on its trigger because it is still editing a field; a menu is a
 *     departure from whatever opened it, and its items are the next thing the
 *     user acts on.
 */

const MIN_HEIGHT = 120;
/* The default floor. A menu is content-sized — `placeAgainstAnchor` treats a
   NUMBER as a minimum and lets the panel grow to its widest label — so this is
   only the point below which a menu looks like an accident. Callers whose
   content is genuinely wider than a list of short labels pass `minWidth`. */
const MIN_WIDTH = 200;

const BdrMenu = ({
  /** the element the menu hangs off — a DOM node, as MUI's anchorEl was */
  anchorEl,
  onClose,
  /**
   * [{ key, label, onClick, header, divider, danger, disabled, keepOpen }]
   *
   *  header    renders as a non-interactive caption. Arrows step over it.
   *  divider   a hairline; carries no label.
   *  danger    for an item that destroys or stops something.
   *  accent    for the affirmative counterpart — Clock In beside Clock Out.
   *            Both exist so a paired action reads at a glance without the
   *            caller reaching for a colour.
   *  onClick   receives the item's own DOM node, for anchoring a submenu.
   *  keepOpen  do NOT close on pick. For an item that swaps the menu's
   *            CONTENTS rather than leaving it — "Assign To Client" turns this
   *            menu into a client search, and a caller whose `onClose` clears
   *            the anchor would otherwise tear the menu down a frame before
   *            its replacement tried to anchor to it.
   *
   * Falsy entries are dropped, so a caller can write
   * `cond && { ... }` inline without filtering.
   */
  items,
  /** labels the menu for screen readers when there is no header row */
  ariaLabel,
  /**
   * Arbitrary content ABOVE the items — a search field, a summary line.
   * For a menu whose top is not a row: the client-assign menu puts its search
   * box here and its results in `items`, so typing and picking stay one
   * surface instead of a field in one popup and a list in another.
   *
   * When present, the menu does NOT steal focus: whatever is in here (an
   * autoFocused input, usually) has a better claim to it than the menu does.
   */
  children,
  /**
   * Nesting level. A submenu opened FROM a menu passes `depth={parent + 1}`.
   *
   * Each level takes its own pair of rungs, so a child's backdrop sits ABOVE
   * its parent's panel: clicking the parent dismisses the child and leaves the
   * parent standing, which is what a submenu should do. Without it both menus
   * land on the same rung, the parent paints over the child's backdrop, and
   * clicking the parent does nothing at all.
   */
  depth = 0,
  /**
   * Widen THIS menu without widening every menu.
   *
   * `placeAgainstAnchor` already content-sizes a menu with a numeric width as
   * the floor, so raising it just gives wide content room to be wide. Added
   * for the Clients menu, whose search results are "Name - email (phone)" and
   * wrapped or clipped at the 200px default while every other menu in the app
   * is a list of two-word labels that should stay narrow.
   *
   * Prefer this over a CSS `min-width` on the panel: the placement maths uses
   * this number to clamp the LEFT edge so a wide menu opened near the right
   * of the window does not start off-screen. CSS cannot tell it that.
   */
  minWidth = MIN_WIDTH,
  /**
   * Highlight the first pickable row as soon as there IS one, so Enter commits
   * it without arrowing first.
   *
   * Off by default, and it should stay off for an ordinary menu: pre-arming a
   * destructive row is how someone deletes a thing by pressing Enter at the
   * wrong moment. It is on for a SEARCH, where the rows are results, the top
   * one is the best match, and type-then-Enter is the expectation the whole
   * interaction is built on.
   */
  autoActiveFirst = false,
  className = "",
}) => {
  const open = Boolean(anchorEl);
  const [position, setPosition] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  /*
   * `onClick` is handed the item's own DOM node. A row that opens a SUBMENU
   * needs something to anchor it to, and the node it was clicked on is the
   * right thing — MUI got this from `event.currentTarget`, which a data-driven
   * item list has no access to.
   */
  const itemEls = useRef({});

  const rows = (items || []).filter(Boolean);
  const isPickable = (row) => !!row && !row.header && !row.divider && !row.disabled;

  const step = (from, delta) => {
    const n = rows.length;
    if (!n) return -1;
    for (let i = 1; i <= n; i += 1) {
      const next = (from + delta * i + n * Math.abs(delta) * n) % n;
      if (isPickable(rows[next])) return next;
    }
    return from;
  };
  const firstPickable = rows.findIndex(isPickable);
  const lastPickable = rows.length - 1 - [...rows].reverse().findIndex(isPickable);

  /*
   * Search mode: keep the highlight on the first pickable row as the rows
   * change, so typing and pressing Enter commits the top match.
   *
   * Keyed on `firstPickable` rather than on `rows`, which is a new array every
   * render — this only fires when the first pickable row actually MOVES (a new
   * result set, or the list emptying), so it cannot fight the arrow keys.
   */
  useEffect(() => {
    if (!open || !autoActiveFirst) return;
    setActiveIndex(firstPickable);
  }, [open, autoActiveFirst, firstPickable]);

  /*
   * ...and when search mode switches OFF while open, drop that highlight.
   *
   * The effect above stops at the switch and leaves `activeIndex` where it
   * was, so the row that index lands on in the NEW rows got armed — in the
   * Clients menu, "+ New Client" after a search that found nothing, which a
   * stray Enter then opened.
   *
   * Only on that transition (hence the previous-value ref): an ordinary menu,
   * whose flag never changes, keeps its arrow / hover position whatever its
   * rows do. A layout effect, so the stale highlight is never painted.
   */
  const prevAutoActiveFirst = useRef(autoActiveFirst);
  useLayoutEffect(() => {
    const wasOn = prevAutoActiveFirst.current;
    prevAutoActiveFirst.current = autoActiveFirst;
    if (open && wasOn && !autoActiveFirst) setActiveIndex(-1);
  }, [open, autoActiveFirst]);

  const place = useCallback(() => {
    if (!anchorEl) return;
    setPosition(
      /* No maxHeight: the menu grows to the bottom of the window and only
         scrolls once it gets there. A fixed cap made a menu opened near the
         top of a tall window scroll with half the screen empty below it. */
      placeAgainstAnchor(anchorEl.getBoundingClientRect(), {
        minHeight: MIN_HEIGHT,
        width: minWidth,
      })
    );
  }, [anchorEl, minWidth]);

  /*
   * Re-place on viewport changes — but NOT while something inside the menu has
   * focus.
   *
   * On a phone, tapping a field inside the menu opens the keyboard, and the
   * keyboard fires `resize`. Re-placing then is actively harmful: the room
   * below the anchor has just collapsed, so `placeAgainstAnchor` can decide to
   * FLIP the panel above the trigger — and the field jumps out from under the
   * finger that is still mid-tap. The focus never lands, and it reads as "the
   * search box needs two taps".
   *
   * The initial `place()` below is deliberately NOT guarded: nothing inside a
   * menu can have focus before it has been positioned.
   */
  const lastWidthRef = useRef(
    typeof window === "undefined" ? 0 : window.innerWidth
  );

  const placeUnlessFocusedWithin = useCallback(() => {
    const width = typeof window === "undefined" ? 0 : window.innerWidth;
    const heightOnly = width === lastWidthRef.current;
    lastWidthRef.current = width;

    /*
     * A HEIGHT-ONLY resize on a menu that contains a field is the on-screen
     * keyboard, and re-placing then is what broke focus.
     *
     * Checking "is something in here focused" is NOT enough, and that was the
     * first attempt at this: on the FIRST tap the focus has not landed yet
     * when `resize` fires, so the guard passed, the panel re-placed, and the
     * field moved out from under the finger. It only ever helped the second
     * tap, which never needed help.
     *
     * Width is the signal that separates the two cases. A keyboard changes the
     * height and nothing else; an orientation change or a real window resize
     * changes the width, and those genuinely should re-place.
     *
     * Scoped to menus that actually contain a field, so every other menu keeps
     * re-placing on any resize exactly as before.
     */
    if (heightOnly && listRef.current?.querySelector("input, textarea")) return;

    /* And never move a menu out from under someone already typing in it. */
    if (listRef.current?.contains(document.activeElement)) return;

    place();
  }, [place]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      setActiveIndex(-1);
      return undefined;
    }
    place();
    // `true` = capture, so this also fires for scrolls inside a modal body,
    // which does not bubble a scroll event to window.
    window.addEventListener("scroll", placeUnlessFocusedWithin, true);
    window.addEventListener("resize", placeUnlessFocusedWithin);
    return () => {
      window.removeEventListener("scroll", placeUnlessFocusedWithin, true);
      window.removeEventListener("resize", placeUnlessFocusedWithin);
    };
  }, [open, place, placeUnlessFocusedWithin]);

  // Bring the highlighted row into view as the arrows move through a long
  // list of results.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    itemEls.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  // Focus the menu so the arrow keys work without the user hunting for it —
  // unless the caller put something focusable in `children`, which wins.
  useEffect(() => {
    if (!open || !position || children) return undefined;
    const raf = requestAnimationFrame(() => listRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open, position, children]);


  const commit = (index) => {
    const row = rows[index];
    if (!isPickable(row)) return;
    if (!row.keepOpen) onClose?.();
    row.onClick?.(itemEls.current[index]);
  };

  /* Arrow keys and Enter drive the menu even while the caret is in a search
     box — so a caller must NOT blanket-stop keydown on that field, or the
     list becomes mouse-only. */
  const fromTextField = (event) => {
    const tag = event.target?.tagName;
    return tag === "INPUT" || tag === "TEXTAREA";
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        // Stop here: a menu opened from inside a modal must not close the
        // modal as well.
        event.stopPropagation();
        onClose?.();
        break;
      case "Tab":
        onClose?.();
        break;
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((i) => step(i, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((i) => step(i, -1));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(firstPickable);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(lastPickable);
        break;
      case " ":
        /*
         * Space COMMITS on a menu row and TYPES in a text field. A menu with a
         * search box in `children` has both, and the keystroke reaches this
         * handler either way, so it has to tell them apart. Everything else
         * printable falls through to `default` and is ignored here — this
         * menu has no typeahead — so space is the only collision.
         */
        if (fromTextField(event)) break;
        event.preventDefault();
        commit(activeIndex);
        break;
      case "Enter":
        event.preventDefault();
        commit(activeIndex);
        break;
      default:
        break;
    }
  };

  if (!open || !position) return null;

  return createPortal(
    <>
      {/*
        A real backdrop, not a document mousedown listener.
        A listener closes the menu but lets the SAME click through to whatever
        is underneath — so dismissing a menu could also select a row, open a
        thread or follow a link. This element is over the page, so the
        dismissing click lands here and goes nowhere else.

        It also carries the blur, which separates the menu from the page
        without dimming it to the point of reading as a modal: this is a
        dismissible popover, not something demanding an answer.
      */}
      <div
        className="bdr-menu-backdrop"
        style={{ "--bdr-menu-depth": depth }}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClose?.();
        }}
        /* The trigger's own click fires after this on the same press; without
           swallowing it the menu would close and immediately reopen. */
        onClick={(event) => event.stopPropagation()}
      />
      <div
      ref={listRef}
      role="menu"
      tabIndex={-1}
      aria-label={ariaLabel}
      className={`bdr-menu ${className}`.trim()}
      style={{ ...position, "--bdr-menu-depth": depth }}
      onKeyDown={handleKeyDown}
      /* See note 2 — without this, clicking an item reads as a backdrop click
         and closes whatever modal is hosting the trigger. */
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {children ? (
        /*
         * Entering the slot clears the items' highlight.
         *
         * `is-active` survives the pointer leaving a row — deliberately, so a
         * keyboard caret is not wiped by a stray mouse movement. But a
         * `children` item highlights on its own `:hover`, so without this the
         * last-hovered item stayed lit and TWO rows looked selected at once.
         * The items and the slot have to know about each other; this is how
         * they do.
         */
        <div
          className="bdr-menu-content"
          onMouseEnter={() => setActiveIndex(-1)}
        >
          {children}
        </div>
      ) : null}
      {rows.map((row, index) => {
        if (row.divider) {
          return <div key={row.key || `d-${index}`} className="bdr-menu-divider" role="separator" />;
        }
        if (row.header) {
          return (
            <div key={row.key || `h-${index}`} className="bdr-menu-header" role="presentation">
              {row.label}
            </div>
          );
        }
        return (
          <button
            key={row.key || `i-${index}`}
            ref={(el) => {
              itemEls.current[index] = el;
            }}
            type="button"
            role="menuitem"
            disabled={row.disabled}
            className={[
              "bdr-menu-item",
              index === activeIndex ? "is-active" : "",
              row.danger ? "is-danger" : "",
              row.accent ? "is-accent" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => commit(index)}
          >
            {row.label}
          </button>
        );
      })}
      </div>
    </>,
    document.body
  );
};

export default BdrMenu;
